import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/outline';
import { createPortal } from 'react-dom';
import useFixedOverlayPosition from './useFixedOverlayPosition';

function ToDoStatusDropdown({ value, statuses, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef();
  const menuRef = useRef();
  const pos = useFixedOverlayPosition(anchorRef, open);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e) => {
      const anchor = anchorRef.current;
      const menu = menuRef.current;
      if (anchor?.contains(e.target) || menu?.contains(e.target)) return;
      setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const selected = useMemo(() => statuses.find((s) => s.id === value), [statuses, value]);
  const ordered = useMemo(() => [...statuses].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), [statuses]);
  const display = selected?.label || value || '—';

  return (
    <div className="relative inline-block w-full">
      <button
        ref={anchorRef}
        className={
          'w-full border rounded-lg px-2 py-1.5 text-sm flex items-center gap-2 justify-between ' +
          (disabled ? 'opacity-60 cursor-not-allowed bg-neutral-50' : 'bg-white hover:bg-slate-50')
        }
        onClick={() => !disabled && setOpen((p) => !p)}
        type="button"
        disabled={disabled}
      >
        <span className="flex items-center gap-2 min-w-0">
          <span
            className="w-2.5 h-2.5 rounded-full shrink-0"
            style={{ backgroundColor: selected?.color || (value ? '#94a3b8' : '#cbd5e1') }}
          />
          <span className="truncate">{display}</span>
        </span>
        <ChevronDownIcon className="w-5 h-5 shrink-0" />
      </button>

      {open && pos
        ? createPortal(
            <div
              ref={menuRef}
              className="fixed z-[9999] bg-white shadow-basic rounded-lg border p-1 max-h-64 overflow-auto"
              style={{
                left: pos.left,
                width: pos.width,
                top: pos.top,
                bottom: pos.bottom,
              }}
            >
              {ordered.map((s) => (
                <button
                  key={s.id}
                  className={
                    'w-full text-left px-2 py-2 rounded-md flex items-center gap-2 text-sm hover:bg-slate-100 ' +
                    (s.id === value ? 'bg-slate-900 text-white hover:bg-slate-900' : '')
                  }
                  type="button"
                  onClick={() => {
                    onChange?.(s.id);
                    setOpen(false);
                  }}
                >
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color || '#94a3b8' }} />
                  <span className="truncate">{s.label || s.id}</span>
                </button>
              ))}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

export default ToDoStatusDropdown;
